import { useTranslation } from 'react-i18next';
import { Card, Icon, Table } from 'semantic-ui-react';
import CopyContentComponent from '../../../misc/CopyContent.component';
import dateToString from '../../../../services/misc/dateToString';
import { kycState } from '../../../../model/details/kycState';
import { IdentificationLog } from '../../../../types/IdentificationLog';
import { IdNowModalComponent } from '../../../misc/IdNowModal.component';
import { useContext, useEffect, useState } from 'react';
import { api } from '../../../../services/fetch/api';
import { AppContext } from '../../../../model/context/app/app';
import { useParams } from 'react-router-dom';
import { signerState } from '../../../../model/details/signer';

type LeadKYCIdNowComponentProps = {
    data: typeof kycState;
    signers: typeof signerState;
    copy?: boolean;
};

export default function LeadKYCIdNowComponent(props: LeadKYCIdNowComponentProps) {
    const { t } = useTranslation();

    const [showModal, setShowModal] = useState(false);
    const [identificationLog, setIdentificationLog] = useState<IdentificationLog[]>([]);
    const context = useContext(AppContext);
    const { leadId } = useParams();

    function createValue(value: string): React.ReactElement {
        return props.copy ? <CopyContentComponent>{value}</CopyContentComponent> : <>{value || ''}</>;
    }

    async function fetchIdentificationLogs() {
        const resp = await fetch(`${api.identificationLogs}/${leadId}`, {
            method: 'GET',
            headers: {
                'Accept-Language': 'de-DE',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${context.token}`,
            },
        });
        if (resp.status === 200) {
            setIdentificationLog(await resp.json());
        }
    }

    useEffect(() => {
        fetchIdentificationLogs();
    }, []);

    return (
        <>
            <Card.Content>
                <Card.Header>{t('lead.detail.box.KYC.idNow.headline')}</Card.Header>
                <Card.Description>
                    <Table celled striped columns={2}>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell width={4}>{t('lead.detail.box.KYC.idNow.status')}</Table.Cell>
                                <Table.Cell width={8} singleLine>
                                    {createValue(props.data.status || 'n/a')}
                                    <Icon
                                        name="info circle"
                                        style={{ cursor: 'pointer', marginLeft: '0.5rem' }}
                                        onClick={() => {
                                            setShowModal(true);
                                        }}
                                    />
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell width={4}>{t('lead.detail.box.KYC.idNow.date')}</Table.Cell>
                                <Table.Cell singleLine>
                                    {createValue(
                                        (props.data.date && dateToString(props.data.date, 'dateLong')) || 'n/a'
                                    )}
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </Card.Description>
            </Card.Content>
            <Card.Content>
                <Card.Header>{t('lead.detail.box.identificationLog.headline')}</Card.Header>
                <Card.Description>
                    <Table celled striped columns={3}>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>{t('lead.detail.box.identificationLog.result')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('lead.detail.box.identificationLog.reason')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('lead.detail.box.identificationLog.timestamp')}</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {identificationLog.length === 0 ? (
                                <Table.Row disabled textAlign={'center'}>
                                    <Table.Cell colSpan={3} disabled={true} textAlign={'center'} singleLine>
                                        {t('lead.detail.box.identificationLog.emptyMessage')}
                                    </Table.Cell>
                                </Table.Row>
                            ) : (
                                identificationLog.map((log, index) => (
                                    <Table.Row key={index}>
                                        <Table.Cell width={1} singleLine>
                                            {log.result}
                                        </Table.Cell>
                                        <Table.Cell width={1} singleLine>
                                            {log.reason || 'n/a'}
                                        </Table.Cell>
                                        <Table.Cell width={4}>
                                            {dateToString(log.timestamp || '', 'dateLong')}
                                        </Table.Cell>
                                    </Table.Row>
                                ))
                            )}
                        </Table.Body>
                    </Table>
                </Card.Description>
            </Card.Content>
            <IdNowModalComponent showModal={showModal} onClose={() => setShowModal(false)}></IdNowModalComponent>
        </>
    );
}
